import React, { useEffect } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useCreateHourPackage,
  useUpdateHourPackage,
} from "../hooks/useHourPackages";
import { PriceWithDiscount } from "../../../components/PriceWithDiscount";

const EMPTY = {
  name: "",
  billing_cycle: "MONTHLY",
  hours_included: "",
  price: "",
  discount_pct: "",
  validity_days: "",
  allowed_absence_hours: "",
  is_active: true,
};

const BILLING_OPTIONS = [
  { value: "MONTHLY", label: "Mensual" },
  { value: "ANNUAL", label: "Anual" },
  { value: "ONE_OFF", label: "Único" },
];

// Preview en vivo mientras el owner escribe — mismo cómputo que
// HourPackage.final_price en el backend (redondeo al peso). Es solo una
// vista previa: el número real que queda guardado siempre lo calcula el
// backend al recibir el POST/PATCH.
function computeFinalPrice(price, discountPct) {
  const p = Number(price) || 0;
  const d = Number(discountPct) || 0;
  return Math.round((p * (100 - d)) / 100);
}

/**
 * Turn a DRF error response into a human message. The backend returns
 * { detail, field_errors: { field: ["msg", ...] } }; surface the first concrete
 * field error so the user knows WHAT was wrong instead of a generic toast.
 */
function extractApiError(error) {
  const data = error?.response?.data;
  if (data?.field_errors && typeof data.field_errors === "object") {
    const firstKey = Object.keys(data.field_errors)[0];
    const msg = firstKey && data.field_errors[firstKey]?.[0];
    if (msg) return `${firstKey}: ${msg}`;
  }
  return data?.detail || data?.message || "Error al guardar el paquete";
}

function HourPackageForm({ show, onHide, editing }) {
  const [values, setValues] = React.useState(EMPTY);

  const createMutation = useCreateHourPackage();
  const updateMutation = useUpdateHourPackage();
  const mutation = editing ? updateMutation : createMutation;

  useEffect(() => {
    // Merge sobre EMPTY para que ningún campo quede undefined (input controlado)
    // cuando `editing` viene de una API que aún no traía ese campo.
    setValues(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [editing, show]);

  const set = (field) => (e) => {
    const val =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [field]: val }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = {
      ...values,
      hours_included: Number(values.hours_included),
      price: Number(values.price),
      discount_pct: values.discount_pct !== "" ? Number(values.discount_pct) : 0,
      validity_days: values.validity_days !== "" ? Number(values.validity_days) : null,
      allowed_absence_hours:
        values.allowed_absence_hours !== "" ? Number(values.allowed_absence_hours) : 0,
    };
    const payload = editing ? { id: editing.id, ...body } : body;
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(editing ? "Paquete actualizado" : "Paquete creado");
        onHide();
      },
      onError: (error) => toast.error(extractApiError(error)),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editing ? "Editar paquete" : "Nuevo paquete de horas"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="pkg-name">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              value={values.name}
              onChange={set("name")}
              placeholder="Ej. Mensual 8h"
              required
              style={{ minHeight: 44 }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="pkg-billing-cycle">
            <Form.Label>Ciclo de facturación</Form.Label>
            <Form.Select
              value={values.billing_cycle}
              onChange={set("billing_cycle")}
              style={{ minHeight: 44 }}
            >
              {BILLING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="pkg-hours">
                <Form.Label>Horas incluidas</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={values.hours_included}
                  onChange={set("hours_included")}
                  placeholder="8"
                  required
                  style={{ minHeight: 44 }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="pkg-price">
                <Form.Label>Precio (COP)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={values.price}
                  onChange={set("price")}
                  placeholder="150000"
                  required
                  style={{ minHeight: 44 }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="pkg-discount">
                <Form.Label>Descuento (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={values.discount_pct}
                  onChange={set("discount_pct")}
                  placeholder="0"
                  style={{ minHeight: 44 }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="pkg-validity">
                <Form.Label>Vigencia (días)</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={values.validity_days}
                  onChange={set("validity_days")}
                  placeholder="30"
                  style={{ minHeight: 44 }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3" controlId="pkg-absence-hours">
            <Form.Label>Horas de inasistencia permitidas</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.5"
              value={values.allowed_absence_hours}
              onChange={set("allowed_absence_hours")}
              placeholder="Ej. 7"
              style={{ minHeight: 44 }}
            />
            <Form.Text muted>
              Horas de clases agendadas y no asistidas que se acumulan sin
              descontar del paquete. Superado el cupo, las horas adicionales se
              descuentan del paquete según la duración de cada clase no asistida.
              El cupo se reinicia con cada paquete.
            </Form.Text>
          </Form.Group>

          {values.price !== "" && (
            <div className="mb-3 p-2 bg-light rounded d-flex align-items-center gap-2">
              <span className="text-muted small">Precio final:</span>
              <PriceWithDiscount
                listPrice={Number(values.price) || 0}
                discountPct={Number(values.discount_pct) || 0}
                finalPrice={computeFinalPrice(values.price, values.discount_pct)}
              />
            </div>
          )}

          <Form.Check
            type="switch"
            id="pkg-is-active"
            label="Activo"
            checked={Boolean(values.is_active)}
            onChange={set("is_active")}
            className="mb-1"
            style={{ minHeight: 44 }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending}
            style={{ minHeight: 44 }}
          >
            {mutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : editing ? (
              "Guardar cambios"
            ) : (
              "Crear paquete"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export { HourPackageForm };
